import {DefaultSettings, settings} from '../../../settings/settings-util';
import h from 'virtual-dom/h';
import {AnyAction, Reducer, Store} from "redux";
import {AlmeState, defaultState} from "../../../wireup/AlmeState";
import {SingleLinkRenderer} from "../../message-renderer-call-to-action/src/models/SingleLinkRenderer";
import {HistoryMessage, HistoryMessageAlme} from "../../../types/history";
import {VChild, VNode} from "virtual-dom";
import {InlineSurveyLink, InlineSurveyResponseAction} from "./models/ResponseActions";
import {LinkSpec} from "../../../settings/button-collection";
import {InlineSurveyLinksActionType} from "./actions/Actions";
import {Dictionary, Mutator} from "../../../types/rendering";

type MutatorModule = { getMutator: () => Mutator };



export default class InlineSurveyLinks
{

    _settings: DefaultSettings;
    _store: Store<AlmeState> | null = null;
    _singleLinkRenderer: SingleLinkRenderer;
    _mutatorModules: MutatorModule[];
    _mutators: Mutator[] = [];


    //#region Public Interface

    constructor(passedSettings: DefaultSettings | null, singleLinkRenderer: SingleLinkRenderer, mutatorModules: MutatorModule[] = [])
    {
        this._settings = passedSettings ?? settings;
        this._singleLinkRenderer = singleLinkRenderer;
        this._mutatorModules = mutatorModules;
    }

    static getDefaultSettings(): DefaultSettings
    {
        return settings;
    }

    initialize(store: Store<AlmeState>): void
    {
        this._store = store;
        this._mutators = this._mutatorModules.reduce((mutators, module) => [...mutators, module.getMutator()], [] as Mutator[]);
    }

    getReducer(): Reducer<AlmeState["Window"]>
    {
        return (state = defaultState.Window, action: AnyAction): AlmeState["Window"] =>
        {
            let newShouldDisable;

            switch (action.type)
            {
                case "DISPLAY_INLINE_SURVEY_LINKS":
                    return {
                        ...state,
                        InlineSurveyLinks: {
                            ...state.InlineSurveyLinks,
                            shouldDisable: {
                                ...state.InlineSurveyLinks.shouldDisable,
                                [(action as InlineSurveyLinksActionType).entryId]: false,
                            },
                        },
                    };

                case "DISABLE_INLINE_SURVEY_LINKS":
                    newShouldDisable = { ...state.InlineSurveyLinks.shouldDisable };
                    delete newShouldDisable[(action as InlineSurveyLinksActionType).entryId];

                    return {
                        ...state,
                        InlineSurveyLinks: {
                            shouldDisable: newShouldDisable,
                        },
                    };

                default:
                    return state;
            }
        };
    }

    getRenderer()
    {
        return (message: HistoryMessage): VChild =>
        {
            const { inlineSurveyResponseActionName } = this._settings.responseActionConstants;
            const almeMessage = message as HistoryMessageAlme;
            const responseAction = almeMessage?.responseActions?.find(action => inlineSurveyResponseActionName.includes(action.Name));
            const entryId = almeMessage?.extensionData.entryId as string;

            if (!responseAction || !entryId) { return []; }

            return [this.renderSurvey(entryId, responseAction as InlineSurveyResponseAction)];
        };
    }

    //#endregion



    //#region Rendering Methods

    renderSurvey(entryId: string, responseAction: InlineSurveyResponseAction): VNode
    {
        const { inlineSurveyYesLink, inlineSurveyNoLink, inlineSurveyYesLinkDisabled,
            inlineSurveyNoLinkDisabled, inlineSurveyLinks, surveyButtonWrap } = this._settings.classNames;
        const { surveyYesIdNameBase, surveyNoIdNameBase } = this._settings.elementIds;
        const { Title, Yes, No } = responseAction.MetaData.InlineSurveyLinks;
        const shouldDisable = this.shouldDisable(entryId);

        const titleNode = h('span', {id:`surveytitle-${entryId}`}, Title);
        const yesNode = this.buildLink(Yes,
            shouldDisable ? inlineSurveyYesLinkDisabled : inlineSurveyYesLink,
            `${surveyYesIdNameBase}${entryId}`,
            entryId,
            responseAction);
        const noNode = this.buildLink(No,
            shouldDisable ? inlineSurveyNoLinkDisabled : inlineSurveyNoLink,
            `${surveyNoIdNameBase}${entryId}`,
            entryId,
            responseAction);
        const buttonWrapNode = h('div', { className: surveyButtonWrap }, [yesNode, noNode]);

        return h('div', { className: inlineSurveyLinks, id: `${this._settings.InlineSurveyLinks.SurveyIdPrefix}${entryId}` }, [titleNode, buttonWrapNode]);
    }

    buildLink(link: InlineSurveyLink, linkClass: string, linkId: string, entryId: string, responseAction: InlineSurveyResponseAction): VNode
    {
        const shouldDisable = this.shouldDisable(entryId);
        let linkSpec: LinkSpec;
        let properties: Dictionary;

        switch (link.ActionType)
        {
            case "AppEvent":
                linkSpec =
                {
                    ...link,
                    className: linkClass,
                    disabled: shouldDisable,
                    parameters: {
                        ...link.parameters,
                        entryId,
                        SurveyUnitLabel: responseAction.SurveyUnitLabel,
                    },
                    tag: "button",
                    attributes:
                    {
                        type:"button",
                        title:link.DisplayText,
                        "aria-labelledby" : `surveytitle-${entryId}`
                    }
                };
                if (linkId) { linkSpec.id = linkId;}
                
                // run mutators to update properties
                linkSpec = this._mutators.reduce((props, mutate) => mutate(props), linkSpec as unknown as Dictionary) as unknown as LinkSpec;

                return this._singleLinkRenderer.renderLink(linkSpec);

            case "ShowSurvey":
                properties =
                {
                    className: linkClass,
                    title: link.DisplayText,
                    onclick: shouldDisable ?
                        (): boolean => false :
                        (): void =>
                        {
                            this._store?.dispatch(
                                {
                                    type: 'SAVE_SURVEY',
                                    EntryId: entryId,
                                    SurveyUnitLabel: responseAction.SurveyUnitLabel,
                                    surveyId: link.SurveyId,
                                    metaData: responseAction.MetaData.Survey,
                                });
                            setTimeout(() =>  this._store?.dispatch({ type: 'SHOW_SURVEY' }), 0);
                        },
                    disabled: shouldDisable,
                    attributes: {
                        'aria-labelledby': `surveytitle-${entryId}`,
                    },
                };

                // run mutators to update properties
                properties = this._mutators.reduce((props, mutate) => mutate(props), properties as unknown as Dictionary) as unknown as Dictionary;

                if (shouldDisable) { properties.tabIndex = -1; }
                if (linkId) { properties.id = linkId;}

                return h('button', properties, link.DisplayText);
        }
    }

    //#endregion



    //#region Helper Methods

    shouldDisable(entryId: string): boolean
    {
        return this._store?.getState().Window.InlineSurveyLinks.shouldDisable[entryId] ?? true;
    }

    //#endregion
}



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/message-renderer-inline-survey-links/src/message-renderer-inline-survey-links.ts